import { api } from "jiffy-api";
import { readCookie } from "common/utils/cookie";

const ENGAGEMENT_THRESHOLD_SECONDS = 10;

class ProductEngagementTracker {
  constructor() {
    this.startTime = Date.now();
    this.tracked = false;
    this.timeoutId = null;
    this.productData = null;
    this.trackingInitialized = false;
    // For composition tracking
    this.compositionTimeoutId = null;
    this.trackedCompositions = new Set();

    // Event listener flags and bound handlers
    this.pageLeaveListenerAdded = false;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  init() {
    if (this.trackingInitialized) return;

    if (!readCookie("name")) return;

    const productContainer = document.querySelector(".js-product-details");
    if (!productContainer) return;

    // Get product data from the container
    this.productData = {
      productId: productContainer.dataset.realProductId || productContainer.dataset.productId,
      compositionName: productContainer.dataset.compositionName,
      pageUrl: window.location.href
    };

    if (!this.productData.productId) return;

    this.trackingInitialized = true;
    this.startTracking();
  }

  startTracking() {
    // Clear any existing timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Set timeout for 10 seconds
    this.timeoutId = setTimeout(() => {
      this.trackEngagement();
    }, ENGAGEMENT_THRESHOLD_SECONDS * 1000);

    // Track when user leaves the page
    this.setupPageLeaveTracking();
  }

  setupPageLeaveTracking() {
    // Only add listeners once to prevent memory leaks
    if (this.pageLeaveListenerAdded) return;

    // Track page visibility change
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Track page unload
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    // Mark listeners as added
    this.pageLeaveListenerAdded = true;
  }

  handleVisibilityChange() {
    if (document.hidden && !this.tracked) {
      this.handlePageLeave();
    } else if (!document.hidden && !this.tracked) {
      // Resume tracking when page becomes visible again
      this.startTime = Date.now();
      this.startTracking();
    }
  }

  handleBeforeUnload() {
    if (!this.tracked) {
      this.handlePageLeave();
    }
  }

  handlePageLeave() {
    // Clear the timeout if user leaves before 10 seconds
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  async trackEngagement() {
    if (this.tracked || !readCookie("name")) return;

    this.tracked = true;

    const engagementData = {
      id: this.productData.productId,
      composition_name: this.productData.compositionName
    };

    try {
      await api.jiffyshirts.trackEngagement.product(engagementData);
    } catch (_error) {
      // Intentionally swallow errors to avoid unhandled promise rejections
    }
  }

  /* eslint-disable max-lines-per-function */
  /**
   * Track a specific composition selection with 10-second timer
   * @param {Object} data - Composition data
   * @param {string} data.compositionId - The composition ID
   * @param {string} data.compositionName - The composition/color name
   * @param {string} data.productId - The product ID
   */
  trackCompositionChange(data) {
    const { compositionId, compositionName, productId } = data;

    this.cancelCompositionTracking();

    if (!readCookie("name") || this.trackedCompositions.has(compositionId)) return;

    this.compositionTimeoutId = setTimeout(async () => {
      this.trackedCompositions.add(compositionId);

      const engagementData = {
        id: productId,
        composition_name: compositionName
      };

      try {
        await api.jiffyshirts?.trackEngagement?.product?.(engagementData);
      } catch (_error) {
        // Intentionally swallow errors to avoid unhandled promise rejections
      }
    }, ENGAGEMENT_THRESHOLD_SECONDS * 1000);
  }
  /* eslint-enable max-lines-per-function */

  /**
   * Cancel the current composition tracking timer
   */
  cancelCompositionTracking() {
    if (this.compositionTimeoutId) {
      clearTimeout(this.compositionTimeoutId);
      this.compositionTimeoutId = null;
    }
  }

  /**
   * Reset composition tracking state
   */
  resetCompositionTracking() {
    this.cancelCompositionTracking();
    this.trackedCompositions.clear();
  }

  destroy() {
    // Clear timeouts
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.cancelCompositionTracking();

    // Remove event listeners if they were added
    if (this.pageLeaveListenerAdded) {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      window.removeEventListener("beforeunload", this.handleBeforeUnload);
      this.pageLeaveListenerAdded = false;
    }

    // Reset all state
    this.tracked = false;
    this.trackingInitialized = false;
    this.trackedCompositions.clear();
  }
}

// Export singleton instance for composition tracking
const instance = new ProductEngagementTracker();

// Export for use in product-details.js
export default ProductEngagementTracker;
export { instance as productEngagementTracker };
